import type { RangePillLabels } from '@ooh/ui/components/filter-panel';

/**
 * Builds the `RangePill` label set (`minLabel`/`maxLabel`/`applyLabel`/`clearLabel`)
 * from any translation function whose namespace exposes the same `filters.*` key
 * names — `inventory` (catalog) and `map` both do. Extracted so the catalog
 * (`use-filter-labels.ts`) and `/map` (`use-map-pins-data.ts`) share ONE
 * implementation instead of two independently hand-rolled objects that can drift
 * out of sync — a `/map`-only regression (RangePill crashing on undefined
 * `rangeLabels`, map-catalog-filter-parity) was caused by exactly that drift.
 *
 * Typed against the exact 4 literal keys it calls (not i18next's generic
 * `TFunction<Namespace>`, and not a plain `(key: string) => string`) —
 * function parameters are contravariant, so a concrete `TFunction<'inventory'>`
 * or `TFunction<'map'>` (whose accepted-key union is namespace-specific) is
 * only assignable to a parameter type the target namespace's key union is a
 * superset of. Both namespaces declare all 4 of these keys.
 */
type RangePillLabelKey =
  | 'filters.rangeMinLabel'
  | 'filters.rangeMaxLabel'
  | 'filters.applyLabel'
  | 'filters.clearLabel';

export function buildRangePillLabels(t: (key: RangePillLabelKey) => string): RangePillLabels {
  return {
    minLabel: t('filters.rangeMinLabel'),
    maxLabel: t('filters.rangeMaxLabel'),
    applyLabel: t('filters.applyLabel'),
    clearLabel: t('filters.clearLabel'),
  };
}
